/**
 * Vzhled: velikost písma (normální / větší / největší).
 * Volba se drží v nastavení a kvůli rychlému startu i v localStorage,
 * aby se písmo nepřeskakovalo po načtení.
 */
import { useEffect, useState } from 'react';
import { getSetting, setSetting } from '../db/db';
import { confirmSmall } from './confirm';
import { Chips, Section } from './ui';

export type TextSize = 'normal' | 'large' | 'xlarge';
const SETTING = 'textSize';
const LS = 'vcely-text-size';

function apply(size: TextSize) {
  if (size === 'normal') delete document.documentElement.dataset.text;
  else document.documentElement.dataset.text = size;
  try {
    localStorage.setItem(LS, size);
  } catch {
    /* nevadí */
  }
}

// Režim na slunci (vysoký kontrast) ------------------------------------------
const CONTRAST = 'contrastSun';
const LS_CONTRAST = 'ulnik-sun';
function applySun(on: boolean) {
  if (on) document.documentElement.dataset.contrast = 'sun';
  else delete document.documentElement.dataset.contrast;
  try {
    localStorage.setItem(LS_CONTRAST, on ? '1' : '0');
  } catch {
    /* nevadí */
  }
}

export function SunSetting() {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    getSetting<boolean>(CONTRAST).then((v) => setOn(!!v));
  }, []);
  if (on == null) return null;
  return (
    <Section title="Režim na slunci">
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          onChange={async (e) => {
            const v = e.target.checked;
            setOn(v);
            applySun(v);
            await setSetting(CONTRAST, v);
            confirmSmall(v ? 'Režim na slunci zapnut' : 'Režim na slunci vypnut');
          }}
        />
        Vysoký kontrast pro práci venku
      </label>
      <p className="muted small">Bílé pozadí, černé písmo a silnější čáry – na přímém slunci se čte nejlépe. Platí i v tmavém režimu telefonu.</p>
    </Section>
  );
}

/** Při startu: nejdřív rychle z localStorage, pak podle uloženého nastavení. */
export function installDisplay() {
  try {
    if (localStorage.getItem(LS_CONTRAST) === '1') applySun(true);
  } catch {
    /* nevadí */
  }
  void getSetting<boolean>(CONTRAST).then((v) => applySun(!!v));
  try {
    const v = localStorage.getItem(LS) as TextSize | null;
    if (v) apply(v);
  } catch {
    /* nevadí */
  }
  void getSetting<TextSize>(SETTING).then((v) => v && apply(v));
}

export function TextSizeSetting() {
  const [size, setSize] = useState<TextSize | null>(null);
  useEffect(() => {
    getSetting<TextSize>(SETTING).then((v) => setSize(v ?? 'normal'));
  }, []);
  if (!size) return null;
  return (
    <Section title="Velikost písma">
      <Chips
        ariaLabel="Velikost písma"
        options={[
          ['normal', 'Normální'],
          ['large', 'Větší'],
          ['xlarge', 'Největší'],
        ]}
        value={size}
        onChange={async (v) => {
          if (!v) return;
          setSize(v);
          apply(v);
          await setSetting(SETTING, v);
          confirmSmall('Písmo změněno');
        }}
      />
      <p className="muted small">Platí pro celou aplikaci. Větší písmo zvětší i tlačítka.</p>
    </Section>
  );
}
